import {
  getMicrosoftAccessToken,
  getMicrosoftRefreshToken,
  getMicrosoftTokenExpiry,
  setMicrosoftTokens,
  clearMicrosoftTokens,
} from './tokenStore';

const PKCE_VERIFIER_KEY = 'ms_oauth_pkce_verifier';
const OAUTH_STATE_KEY = 'ms_oauth_state';

/** sessionStorage, not the in-memory tokenStore: this must survive the full-page
 * redirect to login.microsoftonline.com and back, which a JS variable can't. It's
 * short-lived (cleared on first use) and per-tab, unlike the long-lived bearer
 * tokens tokenStore.js deliberately keeps out of persistent storage. */
function randomUrlSafeString(byteLength) {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function generatePkcePair() {
  const verifier = randomUrlSafeString(32);
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return { verifier, challenge };
}

class MicrosoftAuthService {
  constructor() {
    this.clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    this.redirectUri = process.env.REACT_APP_MICROSOFT_REDIRECT_URI || 'http://localhost:3000/microsoft-callback';
    // Least-privilege: every Graph call this app makes is against /me/drive
    // (see microsoftGraph.service.js) — the previous scope list also
    // requested Files.ReadWrite.All and Sites.ReadWrite.All, tenant-wide
    // write access to every user's files and every SharePoint site, with
    // nothing in the app that uses either (security review SEC-009).
    this.scopes = [
      'Files.ReadWrite',
      'User.Read',
      'offline_access'
    ];
  }

  // Initialize Microsoft authentication
  async initializeAuth() {
    if (!this.clientId) {
      console.error('Microsoft Client ID not configured');
      return { success: false, error: 'Microsoft Client ID not configured' };
    }

    try {
      // Check if we have a stored token
      const storedToken = getMicrosoftAccessToken();
      const tokenExpiry = getMicrosoftTokenExpiry();
      
      if (storedToken && tokenExpiry && new Date().getTime() < parseInt(tokenExpiry)) {
        return { success: true, token: storedToken };
      }

      return { success: false, needsAuth: true };
    } catch (error) {
      console.error('Error initializing Microsoft auth:', error);
      return { success: false, error: error.message };
    }
  }

  // Start Microsoft OAuth flow — PKCE + state (security review SEC-006: the
  // previous authorization request had neither, leaving it open to login CSRF
  // and authorization-code injection via a crafted callback).
  async startAuthFlow() {
    const { verifier, challenge } = await generatePkcePair();
    const state = randomUrlSafeString(24);
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(OAUTH_STATE_KEY, state);

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
      `client_id=${this.clientId}` +
      `&response_type=code` +
      `&redirect_uri=${encodeURIComponent(this.redirectUri)}` +
      `&scope=${encodeURIComponent(this.scopes.join(' '))}` +
      `&response_mode=query` +
      `&state=${encodeURIComponent(state)}` +
      `&code_challenge=${encodeURIComponent(challenge)}` +
      `&code_challenge_method=S256`;

    window.location.href = authUrl;
  }

  // Handle OAuth callback
  async handleCallback(code, state) {
    try {
      const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
      const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
      sessionStorage.removeItem(OAUTH_STATE_KEY);
      sessionStorage.removeItem(PKCE_VERIFIER_KEY);

      if (!expectedState || state !== expectedState) {
        return { success: false, error: 'Sign-in session expired or invalid. Please try again.' };
      }
      if (!verifier) {
        return { success: false, error: 'Sign-in session expired. Please try again.' };
      }

      const tokenResponse = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.clientId,
          scope: this.scopes.join(' '),
          code: code,
          redirect_uri: this.redirectUri,
          grant_type: 'authorization_code',
          code_verifier: verifier,
        }),
      });

      const tokenData = await tokenResponse.json();

      if (tokenData.access_token) {
        // Store tokens
        setMicrosoftTokens({
          accessToken: tokenData.access_token,
          refreshToken: tokenData.refresh_token,
          expiry: new Date().getTime() + (tokenData.expires_in * 1000),
        });

        return { success: true, token: tokenData.access_token };
      } else {
        return { success: false, error: 'Failed to get access token' };
      }
    } catch (error) {
      console.error('Error handling Microsoft callback:', error);
      return { success: false, error: error.message };
    }
  }

  // Refresh access token
  async refreshToken() {
    try {
      const refreshToken = getMicrosoftRefreshToken();

      if (!refreshToken) {
        return { success: false, error: 'No refresh token available' };
      }

      const tokenResponse = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.clientId,
          scope: this.scopes.join(' '),
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      const tokenData = await tokenResponse.json();

      if (tokenData.access_token) {
        // Update stored tokens
        setMicrosoftTokens({
          accessToken: tokenData.access_token,
          refreshToken: tokenData.refresh_token || refreshToken,
          expiry: new Date().getTime() + (tokenData.expires_in * 1000),
        });

        return { success: true, token: tokenData.access_token };
      } else {
        return { success: false, error: 'Failed to refresh token' };
      }
    } catch (error) {
      console.error('Error refreshing Microsoft token:', error);
      return { success: false, error: error.message };
    }
  }

  // Get current access token (with refresh if needed)
  async getAccessToken() {
    try {
      const storedToken = getMicrosoftAccessToken();
      const tokenExpiry = getMicrosoftTokenExpiry();

      if (storedToken && tokenExpiry && new Date().getTime() < parseInt(tokenExpiry)) {
        return { success: true, token: storedToken };
      }

      // Token expired, try to refresh
      const refreshResult = await this.refreshToken();
      if (refreshResult.success) {
        return refreshResult;
      }

      // Refresh failed, need to re-authenticate
      return { success: false, needsAuth: true };
    } catch (error) {
      console.error('Error getting access token:', error);
      return { success: false, error: error.message };
    }
  }

  // Logout from Microsoft
  logout() {
    clearMicrosoftTokens();

    // Redirect to Microsoft logout
    window.location.href = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout';
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = getMicrosoftAccessToken();
    const tokenExpiry = getMicrosoftTokenExpiry();

    return token && tokenExpiry && new Date().getTime() < parseInt(tokenExpiry);
  }
}

export default new MicrosoftAuthService();



